import {PredictionResponse, RulPredictionResponse} from '@mira/api';
import {i18n} from '@mira/i18n';

import {isNil, isNotNil} from 'ramda';

import {MAIN_GROUPS, SUBCATEGORIES} from '../constants/categoryMapping';
import {REPORT_WINDOW_KEY} from '../constants/reportWindow';
import {
  MainCategoryGroupId,
  SubcategoryId,
  TransformedCategory,
  TransformedPredictionData,
} from '../schemas';
import {getSubcategory} from './getSubcategory';
import {transformCategory} from './transformCategory';

function sortByPredictionRelevance(categories: TransformedCategory[]): TransformedCategory[] {
  return [...categories].sort((a, b) => {
    if (a.will_need !== b.will_need) {
      return a.will_need ? -1 : 1;
    }
    if (a.will_need) {
      return b.confidence - a.confidence;
    }
    return a.confidence - b.confidence;
  });
}

export function transformPredictions(
  response: PredictionResponse | null | undefined,
  windowKey: string = REPORT_WINDOW_KEY,
  filterByWillNeed: boolean = true,
  rulResponse?: RulPredictionResponse | null
): TransformedPredictionData {
  const WINDOW_PREDICTIONS = response?.predictions[windowKey];
  const RUL_PREDICTIONS = rulResponse?.rul_predictions;

  if (isNil(WINDOW_PREDICTIONS) && isNil(RUL_PREDICTIONS)) {
    return {
      mainGroups: [],
      totalCategories: 0,
      hasAnyPredictions: false,
    };
  }

  const categoryIds = new Set<string>();
  if (isNotNil(WINDOW_PREDICTIONS)) {
    Object.keys(WINDOW_PREDICTIONS).forEach((id) => categoryIds.add(id));
  }
  if (isNotNil(RUL_PREDICTIONS)) {
    Object.keys(RUL_PREDICTIONS).forEach((id) => categoryIds.add(id));
  }

  const ALL_CATEGORIES = Array.from(categoryIds)
    .map((key) => transformCategory(key, WINDOW_PREDICTIONS?.[key], RUL_PREDICTIONS?.[key]))
    .filter(isNotNil)
    .filter((cat) => !filterByWillNeed || cat.will_need);

  const SUBCATEGORY_CATEGORIES = new Map<SubcategoryId, TransformedCategory[]>();

  ALL_CATEGORIES.forEach((category) => {
    const EXISTING = SUBCATEGORY_CATEGORIES.get(category.subcategoryId) ?? [];
    SUBCATEGORY_CATEGORIES.set(category.subcategoryId, [...EXISTING, category]);
  });

  const ALL_SUBCATEGORIES = SUBCATEGORIES.map((sub) => {
    const CATEGORIES = SUBCATEGORY_CATEGORIES.get(sub.id) ?? [];
    if (CATEGORIES.length === 0) {
      return null;
    }

    return {
      subcategoryId: sub.id,
      title: i18n.t(`subcategory.${sub.id}.title`),
      categories: sortByPredictionRelevance(CATEGORIES),
      count: CATEGORIES.length,
    };
  }).filter((s) => s !== null);

  const MAIN_GROUP_SUBCATEGORIES = new Map<MainCategoryGroupId, typeof ALL_SUBCATEGORIES>();

  ALL_SUBCATEGORIES.forEach((sub) => {
    const MAIN_GROUP_ID = getSubcategory(sub.subcategoryId).mainGroupId;
    const EXISTING = MAIN_GROUP_SUBCATEGORIES.get(MAIN_GROUP_ID) ?? [];
    MAIN_GROUP_SUBCATEGORIES.set(MAIN_GROUP_ID, [...EXISTING, sub]);
  });

  const MAIN_GROUPS_RESULT = MAIN_GROUPS.map((group) => {
    const SUBCATEGORIES_LIST = MAIN_GROUP_SUBCATEGORIES.get(group.id) ?? [];
    if (SUBCATEGORIES_LIST.length === 0) {
      return null;
    }

    const TOTAL_COUNT = SUBCATEGORIES_LIST.reduce((sum, sub) => sum + sub.count, 0);

    return {
      groupId: group.id,
      title: i18n.t(`mainGroup.${group.id}.title`),
      icon: group.icon,
      description: i18n.t(`mainGroup.${group.id}.description`),
      subcategories: SUBCATEGORIES_LIST,
      count: TOTAL_COUNT,
    };
  }).filter((g) => g !== null);

  return {
    mainGroups: MAIN_GROUPS_RESULT,
    totalCategories: ALL_CATEGORIES.length,
    hasAnyPredictions: ALL_CATEGORIES.length > 0,
  };
}
